/**
 * Formats a number to display with K, M, B suffixes for large amounts
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places to show (default: 2)
 * @returns {string} - Formatted string with suffix
 */
export const formatBalance = (value, decimals = 2) => {
  const num = parseFloat(value);
  
  // Handle invalid numbers
  if (isNaN(num) || num === 0) {
    return '0';
  }

  // Handle negative numbers
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // Define the suffixes and their values
  const suffixes = [
    { value: 1e9, suffix: 'B' },   // Billion
    { value: 1e6, suffix: 'M' },   // Million
    { value: 1e3, suffix: 'K' }    // Thousand
  ];

  // Find the appropriate suffix
  for (const { value: threshold, suffix } of suffixes) {
    if (absNum >= threshold) {
      const formatted = (absNum / threshold).toFixed(decimals);
      // Remove trailing zeros after decimal point
      const cleanFormatted = parseFloat(formatted).toString();
      return `${isNegative ? '-' : ''}${cleanFormatted}${suffix}`;
    }
  }

  // For numbers less than 1000, show as is with appropriate decimal places
  if (absNum >= 1) {
    return (isNegative ? -absNum : absNum).toFixed(decimals).replace(/\.?0+$/, '');
  }

  // For very small numbers, show more decimal places
  if (absNum < 0.01) {
    return (isNegative ? -absNum : absNum).toFixed(6).replace(/\.?0+$/, '');
  }

  return (isNegative ? -absNum : absNum).toFixed(decimals).replace(/\.?0+$/, '');
};

/**
 * Formats a balance specifically for cryptocurrency display
 * @param {number|string} balance - The balance to format
 * @param {string} symbol - The cryptocurrency symbol (optional)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted balance string
 */
export const formatCryptoBalance = (balance, symbol = '', decimals = 2) => {
  const formattedBalance = formatBalance(balance, decimals);
  return symbol ? `${formattedBalance} ${symbol}` : formattedBalance;
};

/**
 * Formats a USD value with appropriate suffixes
 * @param {number|string} value - The USD value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted USD string with $ prefix
 */
export const formatUSDBalance = (value, decimals = 2) => {
  const formattedValue = formatBalance(value, decimals);
  return `$${formattedValue}`;
};

export default formatBalance;